import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { Icon } from '@iconify/vue'
import vClickOutside from 'click-outside-vue3'
import veProgress from "vue-ellipse-progress"
import VueLazyLoad from 'vue3-lazyload'
import posthog from "posthog-js";
import { markPosthogReady } from '@/helpers/posthog'
import { sessionStartedAt } from '@/helpers/session'
import { detectTouchDevice, TOUCH_MEDIA_QUERY } from '@/hooks/useDevice'
import { installColumnTrace } from '@/helpers/columnTrace'
import { installCdpIcons } from '@/helpers/iconifyIcons'
import ToastPlugin from 'vue-toast-notification'
import 'vue-toast-notification/dist/theme-default.css'

import { VTooltip } from 'floating-vue'
import 'floating-vue/dist/style.css'
// Quill's stylesheet stays EAGER even though the editor itself is now lazy:
// `_global.css` overrides `.ql-container.ql-snow` and `.ql-toolbar.ql-snow` at
// equal specificity (0,2,0), so whichever file loads last wins. Loading snow.css
// with the dialog chunk would put it after _global.css and undo those overrides.
// It is ~20 kB of CSS; the cost worth removing was the editor's JS.
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import brokenImage from '@/assets/images/broken-portrait.png'

import App from './App.vue'
import router from './router'

/* Global css code */
import './css/main.css'

import { SentryInit } from '@/services/sentry'
import { 
  handleVueError, 
  handleUnhandledRejection, 
  handleGlobalError,
  trackNavigation 
} from '@/services/errorHandler'
import axios from 'axios'
import { vFlipUp } from '@/hooks/vFlipUp'

const SECRET = '2MwNwftfYfzyPMAvG2CtO977STCQCgpRfLVYaPPHC27UeeNkkqjAf1vP+ue1jJ3sN6MqvZ+ubz8vaBDlPz14aaL+NDg8dp2tvoZ1kOt6tSSxE6fB4vdONX2THkVZZVtvGey00tHnHj8y28lHzJ7WVYIZJT9/oR7y7J1fZSLmhjl5CDdQEbqU6mynm2Y7TkyGnVlORORCyvPthFUd/O7TxR/tIMnfeeUR9p5glPC1t3x0l5tHBG1OF3ohSqjquh1XT3nfrCAeD6qUZm832XhUPRYIFXEnsj5vkO+6Y3nbZDg78g5GZgYL3KDQ/M7EvmrJOn7EOZr+8Sp7FcZVTmlIskEwxWp4ZecmNfPWrJbS80PXuM15Ij8KVpklrKuF0YnEUi3bS20oclRgFWfUxMZR8xYT/tfCq/ZYjAePOM39DCitDDJIcgQvJSJyoRKVppsu35zwBo5nil4JzXFoAbXXH3RdlaK0R/SnRZjgABPPKMm6+piN/wem33LSoO/2iFo8bMEbtO+/JUqAxe+ueN3rVaj06ISXv62qhjqBnCTVq6ZXUVhtkCqjxGAHUg2083VWUYYweEr3RMRQO+W0Pr0ptJBRkG1aXF2eVMqh0l+ZMS6cKekoJ7VoJTMfM0djs+AtaUkq+L/ZfB3hKR1lR+mm8CxzunjUZ4qcB3t9c92O1AM='

/* Init Vue app */
const app = createApp(App)

// No-op unless `localStorage.debugColumns === '1'` (see the module).
installColumnTrace()

// Register the custom Figma glyphs so every `icon` / `leadingIcon` prop in the
// design system can address them by name (see helpers/icons.js).
installCdpIcons()

// Stamp when this browsing session began, at BOOT rather than at first read.
// The Inventory-beta welcome prompt compares it against the moment the dealer
// became eligible, and a lazily-written stamp would be later than that moment
// for the exact case the comparison exists to catch: create a batch, then open
// Inventory in the same session.
sessionStartedAt()

// Tag <html> with `is-touch` so device-capability Tailwind variants (`touch:` /
// `no-touch:`) resolve in CSS. Set before mount to avoid a flash of the mobile
// layout on desktop, and kept current if the pointer capability changes.
const applyTouchClass = () => document.documentElement.classList.toggle('is-touch', detectTouchDevice())

applyTouchClass()

window.matchMedia?.(TOUCH_MEDIA_QUERY)?.addEventListener?.('change', applyTouchClass)

const currentURL = window.location.href;

const isLocalOrDev = window.location.hostname === 'localhost' || window.location.hostname.includes('dev')

/* Init Sentry */
!currentURL.includes('localhost') && SentryInit(app)

/* Set up global error handlers */
if (!currentURL.includes('localhost')) {
  // Vue component errors
  app.config.errorHandler = handleVueError

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    handleUnhandledRejection(event.reason)
    // Don't prevent default - let it log to console too
  })

  // Global JavaScript errors
  window.addEventListener('error', handleGlobalError)
}

/* Init Posthog */
posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
  api_host: import.meta.env.VITE_POSTHOG_HOST,
  autocapture: false,
  disable_session_recording: isLocalOrDev ? true : false,
  loaded: () => {
    markPosthogReady()
  },
});

/* Init Iconify component */
app.component('Icon', Icon)

/* Create Vue app */
app.use(router)

// Track navigation for error debugging
if (!currentURL.includes('localhost')) {
  router.afterEach((to, from) => {
    trackNavigation(to, from)
  })
}
app.use(createPinia())
app.use(vClickOutside)
app.use(veProgress)
app.use(VueLazyLoad, {
  lifecycle: {
    error: (el) => {
      const dataImage = el.getAttribute('data-image')
      const imgWithDataId = document.querySelector(`img[data-image="${dataImage}"]`)

      if (imgWithDataId) {
        imgWithDataId.src = brokenImage
      }
    }
  }
})
app.use(ToastPlugin)

app.directive('tooltip', VTooltip)
app.directive('flip-up', vFlipUp)
app.provide("posthog", posthog)

// Fix Flowbite accessibility issue with aria-hidden on focused elements
// This prevents the "Blocked aria-hidden on an element because its descendant retained focus" warning
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
      const target = mutation.target
      if (target.classList.contains('v-popper__popper') && target.hasAttribute('aria-hidden')) {
        // Check if the tooltip itself is focusable (has tabindex) or contains focusable elements
        const isTooltipFocusable = target.hasAttribute('tabindex') && target.getAttribute('tabindex') !== '-1'
        const focusableElements = target.querySelectorAll('button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])')
        
        if (isTooltipFocusable || focusableElements.length > 0) {
          // Remove aria-hidden to prevent accessibility violations
          target.removeAttribute('aria-hidden')
        }
      }
    }
  })
})

// Also fix existing tooltips on page load
const fixExistingTooltips = () => {
  const tooltips = document.querySelectorAll('.v-popper__popper[aria-hidden="true"]')
  tooltips.forEach(tooltip => {
    const isTooltipFocusable = tooltip.hasAttribute('tabindex') && tooltip.getAttribute('tabindex') !== '-1'
    const focusableElements = tooltip.querySelectorAll('button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])')
    
    if (isTooltipFocusable || focusableElements.length > 0) {
      tooltip.removeAttribute('aria-hidden')
      console.log('Fixed accessibility issue: Removed aria-hidden from existing focusable tooltip')
    }
  })
}

// Start observing when the app is mounted
app.mount('#app')

// Fix existing tooltips after mount
setTimeout(fixExistingTooltips, 100)

// Start observing the document for tooltip changes
observer.observe(document.body, {
  attributes: true,
  attributeFilter: ['aria-hidden'],
  subtree: true
})

// Also run fix on DOM content loaded for any tooltips that appear later
document.addEventListener('DOMContentLoaded', () => {
  setTimeout(fixExistingTooltips, 100)
})
